import React, { useEffect, useRef, useCallback, useMemo, useReducer } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as authService from '../services/authService';
import * as userService from '../services/userService';
import * as functionsService from '../services/functionsService';
import * as remoteConfigService from '../services/remoteConfigService';
import * as analyticsService from '../services/analyticsService';
import RotatingBall from "./common/RotatingBall";
import debounce from "lodash/debounce";
import { logger } from "../utils/logger";
import useAppVisibility from "../hooks/useAppVisibility";
import useOnlineStatus from "../hooks/useOnlineStatus";

/**
 * @file Manages the user's session, including authentication state,
 * idle timeouts, and session data like the user's favorite team.
 */

// Create a context to share session data
/**
 * @typedef {object} SessionAuthContextType
 * @property {object|null} currentUser - The current authenticated user object from Firebase.
 * @property {string|null} favoriteTeamId - The user's favorite team ID.
 * @property {boolean} isAdmin - A flag indicating if the user is an admin.
 * @property {boolean} isPremium - A flag indicating if the user has a premium subscription.
 * @property {boolean} isLoadingSession - A flag indicating if the session data is loading.
 * @property {boolean} authLoading - A flag indicating if the authentication state is loading.
 */

/**
 * A React context that provides session data to its children.
 * @type {React.Context<SessionAuthContextType>}
 */
export const SessionAuthContext = React.createContext(null);

const initialState = {
  showTimeoutWarning: false,
  keepLoggedIn: false,
  isSessionLoading: true,
  sessionFavoriteTeamId: null,
  currentUser: undefined, // undefined means auth state is still being determined
  isAdmin: false,
  isPremium: false,
  isAdminStatusFinalized: false,
  firestoreUser: null,
};

function sessionReducer(state, action) {
  logger.log(`SessionReducer: dispatching ${action.type}`, action.payload);
  switch (action.type) {
    case 'AUTH_STATE_CHANGED':
      return { ...state, currentUser: action.payload.user, isAdminStatusFinalized: false, firestoreUser: null, isPremium: false };
    case 'USER_LOGGED_OUT':
      return { ...initialState, currentUser: null, isSessionLoading: false, isAdminStatusFinalized: true, firestoreUser: null, isPremium: false };
    case 'FETCH_SESSION_DATA_START':
      return { ...state, isSessionLoading: true };
    case 'FETCH_SESSION_DATA_SUCCESS':
      return {
        ...state,
        isSessionLoading: false,
        isAdmin: action.payload.isAdmin,
        isPremium: action.payload.isPremium,
        isAdminStatusFinalized: true,
        sessionFavoriteTeamId: action.payload.favoriteTeamId,
        keepLoggedIn: action.payload.keepLoggedIn,
        firestoreUser: action.payload.userProfile,
      };
    case 'FETCH_SESSION_DATA_FAILURE':
      return {
        ...state,
        isSessionLoading: false,
        keepLoggedIn: false,
        sessionFavoriteTeamId: null,
        isPremium: false,
      };
    case 'SHOW_TIMEOUT_WARNING':
      return { ...state, showTimeoutWarning: true };
    case 'HIDE_TIMEOUT_WARNING':
      return { ...state, showTimeoutWarning: false };
    case 'SETTINGS_UPDATED':
      return { ...state, isSessionLoading: true };
    case 'PREMIUM_STATUS_UPDATED':
      return { ...state, isPremium: action.payload.isPremium };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

/**
 * A component that manages the user's session.
 *
 * @param {object} props - The component's props.
 * @param {React.ReactNode} props.children - The child components to render.
 * @returns {JSX.Element} The rendered SessionManager component.
 */
const SessionManager = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const {
    currentUser,
    isSessionLoading,
    isAdmin,
    isPremium,
    isAdminStatusFinalized,
    sessionFavoriteTeamId,
    keepLoggedIn,
    showTimeoutWarning,
    firestoreUser
  } = state;

  const { isVisible, timeSinceLastVisible } = useAppVisibility();
  const isOnline = useOnlineStatus();

  const navigate = useNavigate();
  const location = useLocation();
  const idleTimeout = 30 * 60 * 1000;
  const warningTimeout = 5 * 60 * 1000;
  const idleTimer = useRef(null);
  const warningTimer = useRef(null);
  const hasNavigatedRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const isFetchingSessionDataRef = useRef(false);
  
  const lastPathRef = useRef(location.pathname);
  const pendingNavigationRef = useRef(null);
  const authListenerRef = useRef(null);
  const isMountedRef = useRef(false);

  // More efficient path change detection
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      logger.log(`SessionManager: Route changed from ${lastPathRef.current} to ${location.pathname}`);
      lastPathRef.current = location.pathname;
      pendingNavigationRef.current = null;
    }
  }, [location.pathname]);

  const clearTimeouts = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await authService.logout();
      navigate("/", { replace: true });
      logger.log("SessionManager: User logged out successfully");
    } catch (err) {
      logger.error("SessionManager: Error logging out:", err);
    } finally {
      clearTimeouts();
      dispatch({ type: 'USER_LOGGED_OUT' });
      hasNavigatedRef.current = false;
      pendingNavigationRef.current = null;
      
      if (location.pathname !== "/login") {
        navigate("/login", { replace: true });
      }
    }
  }, [navigate, clearTimeouts, location.pathname]);

  const resetIdleTimer = useCallback(() => {
    if (keepLoggedIn) {
      logger.log("SessionManager: Keep me logged in is true, skipping idle timer reset");
      return;
    }
    logger.log("SessionManager: Resetting idle timer");
    clearTimeouts();
    
    warningTimer.current = setTimeout(() => {
      if (isMountedRef.current) {
        dispatch({ type: 'SHOW_TIMEOUT_WARNING' });
        logger.log("SessionManager: Inactivity warning shown");
      }
    }, idleTimeout - warningTimeout);
    
    idleTimer.current = setTimeout(() => {
      logger.log("SessionManager: Inactivity timeout reached");
      handleLogout();
    }, idleTimeout);
    
    if (showTimeoutWarning) dispatch({ type: 'HIDE_TIMEOUT_WARNING' });
  }, [keepLoggedIn, handleLogout, clearTimeouts, showTimeoutWarning, idleTimeout, warningTimeout]);
  
  const debouncedResetIdleTimer = useMemo(
    () => debounce(resetIdleTimer, 5000),
    [resetIdleTimer]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const handleExtendSession = useCallback(() => {
    logger.log("SessionManager: Extending session");
    if (showTimeoutWarning) dispatch({ type: 'HIDE_TIMEOUT_WARNING' });
    debouncedResetIdleTimer();
    
    if (currentUser) {
      authService.refreshToken(currentUser)
        .then(() => logger.log("SessionManager: Session token refreshed"))
        .catch((err) => logger.error("SessionManager: Error refreshing token:", err));
    }
  }, [debouncedResetIdleTimer, showTimeoutWarning, currentUser]);

  useEffect(() => {
    logger.log("SessionManager: Attaching onAuthChange listener.");

    const unsubscribe = authService.onAuthChange((firebaseUser) => {
      logger.log(`SessionManager: onAuthChange fired. User: ${firebaseUser?.uid}`);
      
      // Check if the user ID has actually changed to prevent unnecessary state updates.
      if (firebaseUser?.uid !== lastUserIdRef.current) {
        logger.log(`SessionManager: User state changed. Old: ${lastUserIdRef.current}, New: ${firebaseUser?.uid}`);
        lastUserIdRef.current = firebaseUser?.uid;

        if (firebaseUser) {
          // New user or user changed. Set the user and reset session data flags.
          dispatch({ type: 'AUTH_STATE_CHANGED', payload: { user: firebaseUser } });
        } else {
          // User logged out.
          dispatch({ type: 'USER_LOGGED_OUT' });
        }
      }
    });

    authListenerRef.current = unsubscribe;

    return () => {
      logger.log("SessionManager: Detaching onAuthChange listener.");
      if (authListenerRef.current) {
        authListenerRef.current();
      }
      clearTimeouts();
    };
  }, [clearTimeouts]);

  // Effect for fetching user data and handling navigation
  useEffect(() => {
    logger.log("SessionManager: useEffect for fetchAndNavigate triggered. currentUser:", currentUser);
    const fetchAndNavigate = async () => {
      // If auth state is still being determined, do nothing yet.
      if (currentUser === undefined) {
        logger.log("SessionManager: Auth state pending, waiting...");
        return;
      }

      // If there is no user, ensure they are on a public page.
      if (!currentUser) {
        logger.log("SessionManager: No user. Ensuring public route.");
        if (!["/login", "/signup", "/"].includes(location.pathname)) {
          navigate("/", { replace: true });
        }
        return;
      }

      // If we are already fetching data for this user, don't start another fetch.
      if (isFetchingSessionDataRef.current) {
        logger.log(`SessionManager: Data fetch already in progress for ${currentUser.uid}.`);
        return;
      }

      logger.log(`SessionManager: Starting session data fetch for user ${currentUser.uid}.`);
      dispatch({ type: 'FETCH_SESSION_DATA_START' });
      isFetchingSessionDataRef.current = true;

      try {
        logger.log("SessionManager: Calling userService.getUserProfileData, authService.getUserAdminStatus, and currentUser.getIdTokenResult");
        
        // Check if plans are enabled before attempting beta user transition
        let transitionPromise = Promise.resolve({ data: { message: "Plans are not enabled, no transition needed." } });
        
        try {
          const plansEnabled = await remoteConfigService.getPlansEnabled();
          logger.log(`SessionManager: Plans enabled: ${plansEnabled}`);
          
          if (plansEnabled) {
            // Only call beta transition if plans are enabled
            transitionPromise = functionsService.handleBetaUserTransition();
          } else {
            logger.log("SessionManager: Plans disabled, skipping beta user transition");
          }
        } catch (configError) {
          logger.error("SessionManager: Error checking plans enabled flag, skipping beta transition:", configError);
        }

        // Run the transition check in parallel with other data fetching, but don't block on it
        const [userProfile, adminClaim, idTokenResult] = await Promise.all([
          userService.getUserProfileData(currentUser.uid),
          authService.getUserAdminStatus(currentUser),
          currentUser.getIdTokenResult(true), // Force refresh
        ]);

        // Handle beta transition result asynchronously (non-blocking)
        transitionPromise.then(transitionResult => {
          if (transitionResult.data.message !== "No transition needed for this user." && 
              transitionResult.data.message !== "Plans are not enabled, no transition needed.") {
            logger.log("SessionManager: Beta user transition completed:", transitionResult.data.message);
            if (transitionResult.data.message === "User transitioned from beta to trial.") {
              analyticsService.logBetaUserTransitioned(currentUser.uid);
            }
          }
        }).catch(transitionError => {
          // Don't fail the entire login flow if transition fails
          logger.error("SessionManager: Beta transition failed, continuing with login:", transitionError);
        });

        // Determine premium status - beta users get premium access during beta period
        let isPremium = idTokenResult.claims.premium === true;
        
        // Check if user is beta and plans are disabled (beta period)
        if (userProfile?.subscriptionStatus === "beta") {
          try {
            const plansEnabled = await remoteConfigService.getPlansEnabled();
            if (!plansEnabled) {
              // During beta period, beta users get premium access
              isPremium = true;
              analyticsService.logBetaUserIdentified(currentUser.uid);
              logger.log("SessionManager: Beta user getting premium access during beta period");
            }
          } catch (configError) {
            logger.error("SessionManager: Error checking plans enabled flag:", configError);
          }
        }

        logger.log("SessionManager: Fetched user data", { userProfile, adminClaim, isPremium, subscriptionStatus: userProfile?.subscriptionStatus });

        const favoriteTeamId = userProfile?.favoriteTeamId || null;
        const keepLoggedIn = userProfile?.keepLoggedIn || false;

        dispatch({
          type: 'FETCH_SESSION_DATA_SUCCESS',
          payload: {
            favoriteTeamId,
            keepLoggedIn,
            isAdmin: adminClaim,
            isPremium,
            userProfile: userProfile, // Pass the entire userProfile object
          },
        });
        logger.log("SessionManager: Dispatched FETCH_SESSION_DATA_SUCCESS");

        // Navigation logic after successful data fetch
        const targetPath = favoriteTeamId ? "/dashboard" : "/my-settings";
        if (["/", "/login", "/signup"].includes(location.pathname)) {
          navigate(targetPath, { replace: true });
        }

      } catch (error) {
        logger.error("SessionManager: Error fetching user session data:", error);
        dispatch({ type: 'FETCH_SESSION_DATA_FAILURE' });
        // If fetching data fails, it might be a permissions issue or stale session.
        // Logging out is a safe recovery path.
        handleLogout(); 
      } finally {
        isFetchingSessionDataRef.current = false;
        logger.log("SessionManager: fetchAndNavigate finally block executed.");
      }
    };

    fetchAndNavigate();
    // This effect should run when the user object changes, or when the user navigates to a page 
    // that requires a redirect (like the root path when logged in).
  }, [currentUser, location.pathname, navigate, handleLogout]);

  // Simplified visibility handling
  useEffect(() => {
    // Only run when app becomes visible after being hidden
    if (isVisible && timeSinceLastVisible > 5000 && currentUser) {
      logger.log(`SessionManager: App visible after ${Math.round(timeSinceLastVisible / 1000)}s in background. Checking session status.`);

      if (!isOnline) {
        logger.warn("SessionManager: App is offline, skipping session refresh.");
        return;
      }

      // Attempt to refresh the token to ensure the session is valid
      authService.checkAndRefreshSession(timeSinceLastVisible)
        .then((result) => {
          if (result?.needsReAuth) {
            logger.warn("SessionManager: Session requires re-authentication. Reloading page.");
            window.location.reload();
          } else if (result) {
            logger.log("SessionManager: Token refresh successful after app became visible.");
          } else {
            logger.log("SessionManager: Session check complete, no refresh was needed.");
          }
        })
        .catch(error => {
          logger.error("SessionManager: Failed to refresh token after app became visible. Forcing page reload.", error);
          // If the refresh fails, it's safer to reload the page to get a clean state
          window.location.reload();
        });
    }
  }, [isVisible, timeSinceLastVisible, currentUser, isOnline]);

  // Regular token refresh for long-lived sessions
  useEffect(() => {
    if (!currentUser || !keepLoggedIn) {
      return;
    }
    
    const tokenRefreshInterval = 45 * 60 * 1000; 
    const intervalId = setInterval(() => {
      if (isMountedRef.current && currentUser && isVisible && isOnline) {
        logger.log("SessionManager: Performing regular token refresh for long-lived session.");
        authService.refreshToken(currentUser).catch(error => {
          logger.error("SessionManager: Regular token refresh failed. Forcing page reload.", error);
          window.location.reload();
        });
      }
    }, tokenRefreshInterval);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [currentUser, keepLoggedIn, isVisible, isOnline]);

  useEffect(() => {
    const handleRedirect = async () => {
      const searchParams = new URLSearchParams(location.search);
      const setupStatus = searchParams.get('setup');
  
      if ((setupStatus === 'complete' || setupStatus === 'success') && currentUser) {
        if (setupStatus === 'success') {
          const creationTime = new Date(currentUser.metadata.creationTime);
          const now = new Date();
          // If the user was created in the last 60 seconds, consider them a new user and don't show the upgrade alert.
          const isNewUser = (now.getTime() - creationTime.getTime()) < 60000;

          if (!isNewUser) {
            alert("Welcome to Fan+! Your upgrade was successful.");
          }
        }
        console.log(`Redirect status '${setupStatus}' detected, refreshing session...`);
  
        // Force a refresh of the auth token to get the latest custom claims.
        const idTokenResult = await currentUser.getIdTokenResult(true);
        const isPremium = idTokenResult.claims.premium === true;
        
        logger.log(`SessionManager: Premium status from token: ${isPremium}`);
        dispatch({ type: 'PREMIUM_STATUS_UPDATED', payload: { isPremium } });

        // Refetch Firestore data to ensure the UI has the latest info.
        logger.log(`SessionManager: Starting session data fetch for user ${currentUser.uid}.`);
        dispatch({ type: 'FETCH_SESSION_DATA_START' });
        isFetchingSessionDataRef.current = true;

        try {
          const [userProfile, adminClaim] = await Promise.all([
            userService.getUserProfileData(currentUser.uid),
            authService.getUserAdminStatus(currentUser),
          ]);

          logger.log("SessionManager: Fetched user data after redirect", { userProfile, adminClaim });

          dispatch({
            type: 'FETCH_SESSION_DATA_SUCCESS',
            payload: {
              favoriteTeamId: userProfile.favoriteTeamId,
              keepLoggedIn: userProfile.keepLoggedIn,
              isAdmin: adminClaim,
              isPremium, // Carry over the premium status from the token
              userProfile: userProfile,
            },
          });
          logger.log("SessionManager: Dispatched FETCH_SESSION_DATA_SUCCESS after redirect");

        } catch (error) {
          logger.error("SessionManager: Error fetching user session data during redirect:", error);
          dispatch({ type: 'FETCH_SESSION_DATA_FAILURE' });
          handleLogout();
        } finally {
          isFetchingSessionDataRef.current = false;
        }

        // Clean the URL to prevent this from running again on a simple page refresh.
        navigate(location.pathname, { replace: true });
      }
    };
  
    handleRedirect();
  }, [location, currentUser, navigate, handleLogout]);

  const sessionAuthContextValue = useMemo(() => ({
    currentUser,
    favoriteTeamId: sessionFavoriteTeamId,
    isAdmin,
    isPremium,
    isLoadingSession: isSessionLoading,
    authLoading: isSessionLoading || (!!currentUser && !isAdminStatusFinalized),
    firestoreUser,
  }), [currentUser, sessionFavoriteTeamId, isAdmin, isPremium, isSessionLoading, isAdminStatusFinalized, firestoreUser]);

  if (currentUser === undefined) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RotatingBall size="w-12 h-12" />
        <span className="ml-4 text-lg text-gray-600">Loading session...</span>
      </div>);
  }

  return (
    <SessionAuthContext.Provider value={sessionAuthContextValue}>
      {children}
      {showTimeoutWarning && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)", display: "flex",
            justifyContent: "center", alignItems: "center", zIndex: 1000,
          }}
        >
          <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "5px", textAlign: "center" }}>
            <p>Your session will expire soon due to inactivity.</p>
            <button
              onClick={handleExtendSession}
              style={{
                padding: "10px 20px", backgroundColor: "#dc143c", color: "#fff",
                border: "none", cursor: "pointer", marginRight: "10px", borderRadius: "4px",
              }}
            >
              Stay Logged In
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: "10px 20px", backgroundColor: "#e0e0e0", color: "#000",
                border: "none", cursor: "pointer", borderRadius: "4px",
              }}
            >
              Log Out
            </button>
          </div>
        </div>
      )}
    </SessionAuthContext.Provider>
  );
};

const MemoizedSessionManager = React.memo(SessionManager);
MemoizedSessionManager.displayName = "SessionManager";
export default MemoizedSessionManager;
